import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';
import { cn } from '../lib/cn.ts';

export const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md',
    'text-sm font-medium transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-border bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      /**
       * ## Why every size grows on a COARSE pointer
       *
       * The WCAG 2.5.5 / iOS HIG floor is 44px, and `sm` is 32px — fine under a
       * mouse, a miss under a thumb. The bump is keyed on `pointer: coarse`
       * rather than on a width breakpoint because WIDTH IS THE WRONG QUESTION: a
       * narrow window on a laptop, a docked devtools pane and a side-by-side
       * split are all mouse-driven and would be "fixed" for nobody, while a
       * landscape phone is 800px wide and still a thumb. `pointer: coarse` asks
       * about the input device, which is what the 44px floor is actually about.
       *
       * The cost is that a desktop browser's responsive-design mode shows none of
       * this — it narrows the viewport but keeps a fine pointer. Check the floor
       * on a device, or with device emulation, not by dragging a window.
       *
       * `min-h`/`min-w` rather than `h`: it raises the floor without overriding
       * a caller's explicit height.
       */
      size: {
        sm: 'h-8 rounded-md px-3 text-xs [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:px-4',
        default: 'h-9 px-4 py-2 [@media(pointer:coarse)]:min-h-11',
        lg: 'h-10 rounded-md px-6 [@media(pointer:coarse)]:min-h-11',
        icon: 'size-9 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    /** Render the single child element instead of a `<button>` (Radix `Slot`). */
    asChild?: boolean;
  };

export function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({ variant, size }), className)} {...props} />;
}
